export interface CaseStudy {
  slug: string;
  name: string;
  headline: string;
  cardHeadline: string;
  industry: string;
  category: string;
  badge: string;
  stack: string[];
  resultMetric: string;
  filterTags: string[];
  relatedService?: string;
  content: {
    problem: string;
    solution: string;
    howItWorks: string[];
    techDetails?: string;
    extraSection?: { title: string; items: string[] };
    results: string[];
  };
}

export const caseStudies: CaseStudy[] = [
  {
    slug: "ai-recommendation-engine",
    name: "AI Recommendation Engine",
    headline: "AI recommendation engine for a mid-size online retailer with 15,000+ SKUs",
    cardHeadline: "AI recommendation engine that learns what your customers actually want",
    industry: "Online Retail",
    category: "eCommerce AI",
    badge: "🛒 eCommerce AI",
    stack: ["Python", "FastAPI", "PostgreSQL", "Redis", "OpenAI Embeddings", "React"],
    resultMetric: "+31% average order value · +28% repeat purchase rate",
    filterTags: ["eCommerce AI"],
    relatedService: "/services/ai-development",
    content: {
      problem: "A mid-size online retailer with 15,000+ SKUs was showing the same \"bestsellers\" block to every visitor. Customers bounced without finding relevant products. The team knew recommendations could help but had no idea where to start — their existing platform had no ML capabilities.",
      solution: "A behavioral recommendation engine that analyzes browsing history, add-to-cart events, purchase sequences, and session patterns to predict what each user is likely to buy next. Built on embedding-based similarity search with a real-time ranking layer.",
      howItWorks: [
        "Every user interaction (view, add to cart, purchase, search query) is logged and vectorized",
        "A similarity model finds products semantically related to the user's recent activity",
        "A ranking layer combines similarity score with inventory levels, margin data, and seasonal demand",
        "Recommendations update in real time — no batch jobs, no stale data",
        "Cold-start problem solved with content-based fallback for new users",
      ],
      techDetails: "Python + FastAPI backend, PostgreSQL + pgvector for embedding storage, Redis for real-time session cache, OpenAI text-embedding-3-small for product vectorization, React components for frontend widgets.",
      results: [
        "+31% average order value within 60 days of launch",
        "+28% repeat purchase rate (customers returning within 30 days)",
        "4.2x click-through rate on recommendation widgets vs. previous static blocks",
        "Recommendation system paid for itself in 6 weeks",
      ],
    },
  },
  {
    slug: "ai-support-assistant",
    name: "AI Customer Support Assistant",
    headline: "AI assistant integrated into Intercom that handles 67% of support tickets automatically",
    cardHeadline: "AI assistant that handles 2/3 of support tickets automatically — no FAQ pages needed",
    industry: "eCommerce / Retail",
    category: "AI Agents",
    badge: "🤖 AI Agent",
    stack: ["Claude API", "LangChain", "Node.js", "PostgreSQL", "Intercom"],
    resultMetric: "67% tickets resolved without human · -40% support workload",
    filterTags: ["AI Agents"],
    relatedService: "/services/ai-agents",
    content: {
      problem: "A growing DTC brand was spending $8,000/month on a 3-person support team. 70% of tickets were variations of the same 20 questions: order status, return policy, delivery timing, product sizing. The team was burning out on repetitive work and response times were slipping.",
      solution: "A context-aware AI assistant integrated directly into their Intercom widget. The assistant understands natural language, has access to the order management system in real time, and knows the full product catalog and policy documentation.",
      howItWorks: [
        "Customer sends a message: \"Where's my order #4821?\"",
        "AI agent calls the OMS API in real time and returns accurate tracking info",
        "If customer asks about returns, the agent checks order eligibility rules and walks them through the process",
        "Unclear or emotional conversations are flagged and routed to a human agent with full context summary",
        "The assistant improves over time — every resolved ticket becomes a training signal",
      ],
      extraSection: {
        title: "What it knows",
        items: [
          "Full order history per customer (via API)",
          "Real-time shipping status",
          "Return and refund policies",
          "Product sizing guides and compatibility info",
          "Promotional terms and conditions",
        ],
      },
      results: [
        "67% of all incoming tickets fully resolved by AI, no human involved",
        "Average first response time: 8 seconds (was 4 hours)",
        "Support team headcount reduced by 1 FTE, redirected to complex cases",
        "Customer satisfaction score increased from 3.8 to 4.6/5",
      ],
    },
  },
  {
    slug: "custom-analytics-dashboard",
    name: "Business Analytics Dashboard",
    headline: "Custom financial dashboard that replaced 4 SaaS subscriptions — built in 3 weeks",
    cardHeadline: "Custom financial dashboard built in 3 weeks — client cancelled 4 SaaS subscriptions",
    industry: "Professional Services / SMB",
    category: "Custom AI Tools",
    badge: "📊 Custom Tool",
    stack: ["Laravel", "Vue.js", "PostgreSQL", "Chart.js", "OpenAI API"],
    resultMetric: "$14,400/year in SaaS eliminated · Built in 3 weeks",
    filterTags: ["Custom AI Tools", "Analytics"],
    relatedService: "/services/custom-development",
    content: {
      problem: "A 40-person professional services company was paying for Fathom, a separate BI tool, a project tracker, and manual Excel reporting — combined cost: $1,200/month. Data lived in 4 different places. The CFO was spending 8 hours a week manually consolidating reports. Decisions were being made on week-old data.",
      solution: "A single unified dashboard that pulls live data from their accounting system, project management tool, and CRM — and uses AI to surface anomalies, generate weekly summaries, and flag budget overruns before they happen.",
      howItWorks: [
        "Live data sync from QuickBooks, HubSpot, and Asana via API",
        "Cash flow, P&L, and project economics in one view — updated daily",
        "AI layer (GPT-4) generates a weekly written summary: \"Revenue is up 12% vs last month. Project X is 18% over budget. 3 invoices totaling $24K are overdue.\"",
        "Anomaly detection flags unusual expenses or sudden drops in revenue",
        "Role-based access: CEO sees the full picture, project managers see only their projects",
      ],
      results: [
        "4 SaaS subscriptions cancelled: $14,400/year saved",
        "CFO reporting time: 8 hours/week → 45 minutes/week",
        "First budget overrun caught proactively (saving ~$30K)",
        "Total build cost: recovered in 4 months",
      ],
    },
  },
  {
    slug: "order-processing-automation",
    name: "Automated Order Processing",
    headline: "Fully automated pipeline processing 900 orders/day without manual input",
    cardHeadline: "900 orders a day — processed, routed, and confirmed without a single person touching them",
    industry: "Wholesale / B2B eCommerce",
    category: "Automation",
    badge: "⚡ Automation",
    stack: ["Laravel", "Python", "OpenAI", "PostgreSQL"],
    resultMetric: "900 orders/day automated · 3 FTE redeployed",
    filterTags: ["Automation"],
    relatedService: "/services/ai-development",
    content: {
      problem: "A B2B wholesale distributor was processing 900+ orders per day. Each order required: verifying stock availability, calculating shipping, sending confirmation, updating the warehouse system, generating an invoice, and notifying the account manager. With a team of 5 handling this manually, errors were frequent and overtime was the norm.",
      solution: "A fully automated order processing pipeline. From the moment an order is submitted to the moment the warehouse receives the pick list — zero human involvement required for standard orders.",
      howItWorks: [
        "Order received (WooCommerce, email, or EDI feed)",
        "AI parses order details — including unstructured email orders (\"please send 200 units of SKU-4421 to our Berlin warehouse\")",
        "System checks stock, calculates shipping rules, applies correct pricing tier",
        "Confirmation sent to customer automatically with accurate delivery window",
        "Warehouse system updated and pick list generated",
        "Invoice created and sent",
        "Account manager notified only if order requires attention (custom requirements, credit hold, etc.)",
        "Edge case handling: If the AI is less than 95% confident on any step, the order is flagged for human review with a pre-filled suggestion — the operator confirms in one click",
      ],
      results: [
        "94% of orders fully processed without human input",
        "3 staff members redeployed from order processing to sales and account management",
        "Order confirmation time: 4 hours → 90 seconds",
        "Error rate dropped from 3.2% to 0.4%",
        "System paid for itself in 7 weeks",
      ],
    },
  },
  {
    slug: "ai-knowledge-base",
    name: "AI-Powered Knowledge Base",
    headline: "Internal AI assistant that searches all company docs and answers employee questions instantly",
    cardHeadline: "Ask it anything about your company — it actually knows the answer",
    industry: "SaaS / Tech Company",
    category: "AI Agents",
    badge: "🧠 AI Agent",
    stack: ["Claude API", "LlamaIndex", "Pinecone", "React", "Node.js"],
    resultMetric: "Question resolution time -82% · Onboarding cut in half",
    filterTags: ["AI Agents"],
    relatedService: "/services/ai-agents",
    content: {
      problem: "A 120-person SaaS company had documentation scattered across Notion, Google Drive, Confluence, and Slack. New employees spent their first 3 weeks asking senior engineers basic questions. Experienced staff lost hours each week re-explaining the same processes. \"Just search Notion\" wasn't working.",
      solution: "An internal AI assistant that ingests all documentation (Notion, Confluence, Drive, Slack threads, code comments) and lets employees ask natural language questions — getting accurate, sourced answers in seconds.",
      howItWorks: [
        "All documentation sources connected and continuously synced",
        "Content chunked, embedded, and stored in Pinecone vector database",
        "Employee asks: \"What's the process for requesting infrastructure changes?\" → AI retrieves relevant docs, generates a clear answer, and links to the source",
        "Citation mode: every answer shows which document it came from",
        "\"I don't know\" mode: if confidence is low, the AI says so and suggests who to ask",
        "Feedback loop: employees rate answers, which improves retrieval over time",
      ],
      results: [
        "Average time to find an answer: 4 hours → 20 minutes",
        "New employee onboarding time reduced by 50%",
        "340 Slack questions per week reduced to 80",
        "Engineering team reclaimed ~6 hours/week previously spent answering internal questions",
      ],
    },
  },
  {
    slug: "ai-lead-qualification",
    name: "AI Lead Qualification & CRM",
    headline: "AI-powered lead scoring and qualification that briefs sales reps before the first call",
    cardHeadline: "AI qualifies leads, scores them, and briefs your sales rep before the first call",
    industry: "B2B SaaS / Sales",
    category: "Automation",
    badge: "⚡ Automation",
    stack: ["GPT-4", "Node.js", "HubSpot API", "PostgreSQL", "Slack"],
    resultMetric: "Sales time on qualified leads +60% · Response: 3 days → 4 min",
    filterTags: ["Automation"],
    relatedService: "/services/ai-agents",
    content: {
      problem: "A B2B SaaS company was getting 200 inbound leads per month. Sales reps were spending 40% of their time on leads that were never going to convert — wrong company size, wrong industry, no budget. Good leads were getting responses 3 days after filling out the form.",
      solution: "An AI-powered qualification layer that activates the moment a lead submits a form. The system researches the company, scores the lead, and briefs the sales rep — all before a human is involved.",
      howItWorks: [
        "Lead submits a form with their name, company, and email",
        "AI agent looks up the company (LinkedIn, Clearbit, website scraping) in real time",
        "Scores the lead on: company size, industry fit, tech stack, growth signals, and intent indicators",
        "Generates a 3-paragraph briefing: \"This is a 85-person fintech startup using Stripe and Segment. Raised Series A in 2023. High fit for your analytics product.\"",
        "High-score leads get an automated personalized email within 4 minutes",
        "Low-score leads get a nurture sequence",
        "Sales rep receives Slack notification with full context before picking up the phone",
      ],
      results: [
        "Lead response time: 3 days → 4 minutes (for high-score leads)",
        "Sales team time spent on qualified leads increased by 60%",
        "22% increase in demo booking rate",
        "Pipeline quality score (deals closed / demos booked) up 35%",
      ],
    },
  },
  {
    slug: "ai-document-processing",
    name: "AI Document Processing Pipeline",
    headline: "Document intelligence pipeline processing 800 documents/day with 91% full automation",
    cardHeadline: "800 documents a day — read, extracted, validated, and filed. Automatically.",
    industry: "Legal / Finance / Operations",
    category: "AI Agents",
    badge: "🤖 AI Agent",
    stack: ["Claude API", "Python", "FastAPI", "PostgreSQL", "AWS S3"],
    resultMetric: "800 docs/day processed · Review time -91%",
    filterTags: ["AI Agents"],
    relatedService: "/services/ai-development",
    content: {
      problem: "A financial services firm was receiving 800+ documents per day: invoices, contracts, compliance forms, and bank statements. A team of 6 data entry operators spent their days reading PDFs and typing data into a system. Errors were common. Backlogs of 3–4 days were normal.",
      solution: "A document intelligence pipeline. Upload a PDF → AI reads it → extracts structured data → validates it against business rules → routes it to the right system. Humans review only the exceptions.",
      howItWorks: [
        "Documents arrive via email attachment, upload portal, or API",
        "Claude extracts: vendor name, amounts, dates, line items, contract terms, signatures",
        "Validation layer checks: does the vendor exist in our system? Does the total match the line items? Is the contract date within the valid window?",
        "Passes → auto-filed into the correct folder and system record",
        "Fails validation → flagged with specific reason, routed to human with pre-filled correction form",
        "Full audit trail maintained for compliance",
      ],
      extraSection: {
        title: "Handles",
        items: [
          "Invoices in 12 languages",
          "Scanned PDFs (OCR layer included)",
          "Non-standard formats",
          "Handwritten annotations",
        ],
      },
      results: [
        "91% of documents processed without human input",
        "Processing time: 2–3 days backlog → real-time (under 2 minutes)",
        "Data entry team of 6 reduced to 1 quality control reviewer",
        "Error rate: 4.1% → 0.3%",
      ],
    },
  },
  {
    slug: "ai-dynamic-pricing",
    name: "AI Dynamic Pricing Engine",
    headline: "Dynamic pricing engine that monitors competitors, demand, and inventory to optimize prices automatically",
    cardHeadline: "Prices that update themselves — based on demand, competition, and inventory",
    industry: "eCommerce / Travel / Hospitality",
    category: "eCommerce AI",
    badge: "🛒 eCommerce AI",
    stack: ["Python", "FastAPI", "PostgreSQL", "Redis", "React", "Custom ML"],
    resultMetric: "+18% gross margin · +11% revenue on competitive SKUs",
    filterTags: ["eCommerce AI"],
    relatedService: "/services/ai-development",
    content: {
      problem: "A large online retailer with 8,000 SKUs was pricing products manually. Price reviews happened monthly. By the time a price change was approved, market conditions had shifted. They were consistently underselling on high-demand items and overpriced on slow movers. Competitors were adjusting prices daily.",
      solution: "A dynamic pricing engine that monitors market signals, internal inventory levels, and demand patterns — and updates prices automatically within guardrails set by the business team.",
      howItWorks: [
        "Competitor price monitoring: checks 12 competitor sites for overlapping SKUs every 4 hours",
        "Demand forecasting: analyzes historical sales velocity, seasonal patterns, and recent search trends",
        "Inventory pressure: if stock is high and turnover is slow → gradual price reduction. If critically low → price stabilizes to extend availability",
        "Margin floor protection: no price ever goes below the minimum margin threshold set by the team",
        "A/B testing layer: 10% of price changes are tested before full rollout",
        "Human override: merchandising team can freeze any category or SKU with one click",
      ],
      results: [
        "+18% gross margin across repriced SKUs",
        "+11% revenue on competitive product lines within 90 days",
        "140 hours/month of manual pricing work eliminated",
        "Price competitiveness score improved from 34% to 71%",
      ],
    },
  },
];

export const caseCategories = ["All", "AI Agents", "Automation", "eCommerce AI", "Analytics", "Custom AI Tools"];
